import { SegmentedControl, SegmentedControlItem } from '@mantine/core';
import { IconLogout, IconUser } from '@tabler/icons-react';
import { AppIcons } from '../../ui/app-icons';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { Constant } from '../../../models/constant';
import { UserRoles } from '../../../models/user';
import { AppCtx } from '../../../provider/AppContext';
import { apiService } from '../../../services/api.service';
import classes from './NavbarSegmented.module.css';

const tabs = {
    general: [
        { link: '/home', label: 'Home', icon: AppIcons.home },
        { link: '/contratti', label: 'Contratti', icon: AppIcons.contract },
        { link: '/mad', label: 'M.A.D.', icon: AppIcons.mad },
        { link: '/buoni', label: 'Buoni di carico', icon: AppIcons.loadingOrder },
        { link: '/navi', label: 'Navi', icon: AppIcons.ship },
        { link: '/report-magazzinaggio', label: 'Report magazzinaggio', icon: AppIcons.storageReport },
        { link: '/riepiloghi', label: 'Riepiloghi clienti', icon: AppIcons.customerReport },
    ],
    admin: [
        { link: '/listini', label: 'Listini', icon: AppIcons.priceList },
        { link: '/oneri', label: 'Oneri e tasse', icon: AppIcons.tax },
        { link: '/admin/impostazioni', label: 'Impostazioni', icon: AppIcons.settings },
        { link: '/admin/utenti', label: 'Utenti', icon: AppIcons.users },
    ],
};

type NavbarSection = keyof typeof tabs;

export function NavbarSegmented() {
    const [section, setSection] = useState<NavbarSection>();
    const [sections, setSections] = useState<SegmentedControlItem[]>([]);
    const location = useLocation();
    const appContex = useContext(AppCtx);

    useEffect(() => {
        if (!appContex.user)
            return;

        let sects: SegmentedControlItem[] = [
            { label: 'Gestione', value: 'general' },
        ];

        if (appContex.user.role === UserRoles.Admin)
            sects.push({ label: 'Admin', value: 'admin' });

        setSections(sects);
    }, [appContex.user])

    // La sezione attiva si deduce dall'indirizzo, così arrivando su una pagina per link
        // diretto il menu si posiziona da solo.
    useEffect(() => {
        const match = (Object.keys(tabs) as NavbarSection[])
            .find(key => tabs[key].some(item => location.pathname?.startsWith(item.link)));

        setSection(match ?? 'general');
    }, [location.pathname])

    //const links2 = tabs[section].map((item) => <LinksGroup {...item} key={item.label} />);
    const getLinks = useCallback(() => {
        if (!section)
            return;

        // Link di react-router e non un <a href>: quello faceva ricaricare l'intera
        // applicazione a ogni voce di menu, azzerando lo stato e rileggendo tutto da capo.
        const links = tabs[section].map((item) => (
            <Link
                className={classes.link}
                data-active={location.pathname?.startsWith(item.link) || undefined}
                to={item.link}
                key={item.label}
            >
                <item.icon className={classes.linkIcon} stroke={1.5} />
                <span>{item.label}</span>
            </Link>
        ));

        return links;
    }, [section, location.pathname]);

    const onLogoutClick = async () => {
        appContex.setShowLoader(true);
        try {
            // La sessione locale va chiusa comunque: se la chiamata fallisce l'utente
            // deve poter uscire lo stesso. Prima un setTimeout fisso di un secondo
            // faceva da attesa arbitraria e ignorava del tutto l'esito.
            await apiService.Logout().catch(() => undefined);
        } finally {
            localStorage.removeItem(Constant.LS_ACCESS_TOKEN_KEY);
            localStorage.removeItem(Constant.LS_USER_KEY);
            appContex.setIsLogged(false);
            appContex.setShowLoader(false);
        }
    }

    return (
        <nav className={classes.navbar}>
            <div>
                <SegmentedControl
                    value={section}
                    onChange={(value: any) => setSection(value)}
                    transitionTimingFunction="ease"
                    fullWidth
                    data={sections}
                />
            </div>

            <div className={classes.navbarMain}>{getLinks()}</div>

            <div className={classes.footer}>
                <a href="/profilo" className={classes.link}
                    data-active={location.pathname?.endsWith('/profilo') || undefined}>
                    <IconUser className={classes.linkIcon} stroke={1.5} />
                    <span>Profilo</span>
                </a>

                <a href="#" className={classes.link} onClick={onLogoutClick}>
                    <IconLogout className={classes.linkIcon} stroke={1.5} />
                    <span>Logout</span>
                </a>
            </div>
        </nav>
    );
}
